import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  notification,
  Row,
  Col,
  Typography,
  Space,
  Spin,
  Result,
  InputNumber,
  Divider,
  Avatar
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { baseUrl } from '../config';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function PublicRegistrationForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const submitRegistration = async (values) => {
    try {
      setLoading(true);
      
      const formattedData = {
        ...values,
        dob: values.dob ? moment(values.dob).format('YYYY-MM-DD') : null,
        amount: values.amount || 0
      };

      const response = await fetch(`${baseUrl}/registration/public-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData)
      });

      const result = await response.json();
      
      if (result.status) {
        return { success: true, data: result.data };
      } else {
        throw new Error(result.message || 'Registration failed');
      }
    } catch (error) {
      throw new Error(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      const result = await submitRegistration(values);
      
      if (result.success) {
        setSubmittedData(result.data);
        setSubmitted(true);
        notification.success({
          message: 'Registration Successful!',
          description: 'Thank you for registering. Your information has been recorded.',
          placement: 'top',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Registration Failed',
        description: error.message,
        placement: 'top',
      });
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setSubmittedData(null);
    form.resetFields();
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return `₹${Number(value).toLocaleString()}`;
  };

  const getCurrentDateTime = () => {
    return moment().format('DD MMM YYYY, hh:mm A');
  };

  // Mobile-optimized styles
  const mobileStyles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: window.innerWidth <= 768 ? '10px' : '20px'
    },
    formContainer: {
      maxWidth: window.innerWidth <= 768 ? '100%' : '1000px',
      margin: '0 auto'
    },
    headerCard: {
      marginBottom: window.innerWidth <= 768 ? '16px' : '24px',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      textAlign: 'center',
      padding: window.innerWidth <= 768 ? '16px' : '24px'
    },
    formCard: {
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      padding: window.innerWidth <= 768 ? '16px' : '24px'
    },
    mobileInput: {
      height: window.innerWidth <= 768 ? '48px' : '40px',
      fontSize: window.innerWidth <= 768 ? '16px' : '14px',
      borderRadius: '8px'
    },
    mobileButton: {
      height: window.innerWidth <= 768 ? '56px' : '50px',
      fontSize: window.innerWidth <= 768 ? '18px' : '16px',
      fontWeight: '600',
      minWidth: window.innerWidth <= 768 ? '100%' : '200px'
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div style={mobileStyles.container}>
        <Card style={{
          maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
          width: '100%',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          margin: '0 auto'
        }}>
          <Result
            status="success"
            title={
              <Title level={window.innerWidth <= 768 ? 3 : 2} style={{ 
                color: '#52c41a', 
                marginBottom: '16px',
                fontSize: window.innerWidth <= 768 ? '20px' : '24px'
              }}>
                Registration Completed!
              </Title>
            }
            subTitle={
              <div>
                <Paragraph style={{ 
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px', 
                  marginBottom: '20px' 
                }}>
                  Thank you <Text strong style={{ color: '#1890ff' }}>{submittedData?.name}</Text> for registering.
                  You will be contacted soon.
                </Paragraph>
              </div>
            }
            extra={[
              <Button 
                type="primary" 
                size="large"
                onClick={resetForm}
                style={{
                  ...mobileStyles.mobileButton,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px'
                }}
              >
                Register Another Person
              </Button>
            ]}
          />

          {submittedData && (
            <Card 
              size="small" 
              style={{ 
                background: '#f8f9fa', 
                border: 'none',
                borderRadius: '8px',
                marginTop: '20px'
              }}
              title={
                <Text strong style={{ 
                  color: '#1890ff',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px'
                }}>
                  Registration Summary
                </Text>
              }
            >
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                    Name:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                    {submittedData.name}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                    Phone:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                    {submittedData.phone_no}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                    Amount:
                  </Text>
                  <br />
                  <Text strong style={{ 
                    color: '#52c41a',
                    fontSize: window.innerWidth <= 768 ? '14px' : '16px'
                  }}>
                    {formatCurrency(submittedData.amount)}
                  </Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: window.innerWidth <= 768 ? '12px' : '14px' }}>
                    Time:
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                    {getCurrentDateTime()}
                  </Text>
                </Col>
              </Row>
            </Card>
          )}
        </Card>
      </div>
    );
  }

  // Registration Form
  return (
    <div style={mobileStyles.container}>
      <div style={mobileStyles.formContainer}>
        {/* Header Card */}
        <Card style={mobileStyles.headerCard}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Avatar
              size={window.innerWidth <= 768 ? 60 : 80}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
              icon={<UserOutlined style={{ fontSize: window.innerWidth <= 768 ? '24px' : '36px' }} />}
            />
            
            <div>
              <Title level={window.innerWidth <= 768 ? 2 : 1} style={{ 
                margin: 0, 
                color: '#1890ff',
                fontSize: window.innerWidth <= 768 ? '24px' : '32px'
              }}>
                Registration Form
              </Title>
              <Paragraph style={{ 
                fontSize: window.innerWidth <= 768 ? '14px' : '16px', 
                color: '#666', 
                margin: '8px 0 0 0' 
              }}>
                Please fill in your details
              </Paragraph>
            </div>
          </Space>
        </Card>

        {/* Form Card */}
        <Card style={mobileStyles.formCard}>
          <Spin spinning={loading} tip="Submitting...">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              size="large"
            >
              {/* All fields in single column on mobile, two columns on desktop */}
              <Row gutter={window.innerWidth <= 768 ? [0, 16] : [24, 16]}>
                {/* Phone Number */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Phone Number *
                    </Text>}
                    name="phone_no"
                    rules={[
                      { required: true, message: 'Please enter your phone number' },
                      { pattern: /^\d{10,12}$/, message: 'Phone must be 10-12 digits!' }
                    ]}
                  >
                    <Input
                      prefix={<PhoneOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter phone number"
                      style={mobileStyles.mobileInput}
                      maxLength={12}
                    />
                  </Form.Item>
                </Col>

                {/* Full Name */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Full Name *
                    </Text>}
                    name="name"
                    rules={[
                      { required: true, message: 'Please enter your full name' },
                      { min: 2, message: 'Name must be at least 2 characters' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter your full name"
                      style={mobileStyles.mobileInput}
                    />
                  </Form.Item>
                </Col>

                {/* Father's Name */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Father's Name
                    </Text>}
                    name="father_name"
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter father's name"
                      style={mobileStyles.mobileInput}
                    />
                  </Form.Item>
                </Col>

                {/* Date of Birth */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Date of Birth
                    </Text>}
                    name="dob"
                  >
                    <DatePicker
                      style={{
                        width: '100%',
                        ...mobileStyles.mobileInput
                      }}
                      placeholder="Select date"
                      format="DD-MM-YYYY"
                      disabledDate={(current) => current && current > moment().endOf('day')}
                      suffixIcon={<CalendarOutlined style={{ color: '#1890ff' }} />}
                    />
                  </Form.Item>
                </Col>

                {/* Village */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Village
                    </Text>}
                    name="village"
                  >
                    <Input
                      prefix={<HomeOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter village"
                      style={mobileStyles.mobileInput}
                    />
                  </Form.Item>
                </Col>

                {/* District */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      District
                    </Text>}
                    name="district"
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter district"
                      style={mobileStyles.mobileInput}
                    />
                  </Form.Item>
                </Col>

                {/* Occupation Address */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Work Address
                    </Text>}
                    name="occ_address"
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: '#1890ff' }} />}
                      placeholder="Enter work address"
                      style={mobileStyles.mobileInput}
                    />
                  </Form.Item>
                </Col>

                {/* Amount Pledge */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Amount Pledge
                    </Text>}
                    name="amount"
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                        ...mobileStyles.mobileInput
                      }}
                      placeholder="Enter amount"
                      formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/₹\s?|(,*)/g, '')}
                      min={0}
                      max={10000000}
                    />
                  </Form.Item>
                </Col>

                {/* Remark */}
                <Col xs={24}>
                  <Form.Item
                    label={<Text strong style={{ fontSize: window.innerWidth <= 768 ? '14px' : '16px' }}>
                      Additional Notes
                    </Text>}
                    name="remark"
                  >
                    <TextArea
                      placeholder="Enter any additional information"
                      rows={window.innerWidth <= 768 ? 3 : 4}
                      maxLength={500}
                      showCount
                      style={{
                        borderRadius: '8px',
                        fontSize: window.innerWidth <= 768 ? '16px' : '14px'
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {window.innerWidth > 768 && <Divider />}

              {/* Submit Section */}
              <div style={{ textAlign: 'center', marginTop: window.innerWidth <= 768 ? '24px' : '32px' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    icon={<SendOutlined />}
                    style={{
                      ...mobileStyles.mobileButton,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                    }}
                    block={window.innerWidth <= 768}
                  >
                    {loading ? 'Submitting...' : 'Submit Registration'}
                  </Button>
                  
                  <Card 
                    size="small" 
                    style={{ 
                      background: '#f0f7ff', 
                      border: '1px solid #d6e4ff',
                      borderRadius: '8px',
                      margin: '0 auto'
                    }}
                  >
                    <Text style={{ 
                      color: '#666', 
                      fontSize: window.innerWidth <= 768 ? '12px' : '14px' 
                    }}>
                      <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                      Your information is secure and will be used only for registration.
                    </Text>
                  </Card>
                </Space>
              </div>
            </Form>
          </Spin>
        </Card>
      </div>
    </div>
  );
}

export default PublicRegistrationForm;